import { useState } from 'react'
import { isValidPhoneNumber } from 'react-phone-number-input'
import PhoneInput from 'react-phone-number-input/input'
import { Api } from '../api'

const GoogleG = () => (
  <span className="flex items-center justify-center w-8 h-8 bg-white rounded-md shrink-0 border border-stone-200">
    <svg viewBox="0 0 24 24" className="w-4 h-4" xmlns="http://www.w3.org/2000/svg">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  </span>
)

type PhoneStep = 'entry' | 'code'

interface Props {
  onLogin: (username: string, password: string) => Promise<void>
  onGoogleSignIn: () => void
  onPhoneLogin: () => void
}

export default function LoginPage({ onLogin, onGoogleSignIn, onPhoneLogin }: Props) {
  const [showPassword, setShowPassword] = useState(false)
  const [showPhone, setShowPhone]       = useState(false)
  const [username, setUsername]         = useState('')
  const [password, setPassword]         = useState('')
  const [error, setError]               = useState<string | null>(null)
  const [loading, setLoading]           = useState(false)

  // Phone sign-in state
  const [phoneStep, setPhoneStep]   = useState<PhoneStep>('entry')
  const [phone, setPhone]           = useState('')
  const [code, setCode]             = useState('')
  const [phoneError, setPhoneError] = useState<string | null>(null)
  const [phoneLoading, setPhoneLoading] = useState(false)
  const phoneIsValid = isValidPhoneNumber(phone, 'US')

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      await onLogin(username, password)
    } catch {
      setError('Invalid credentials')
    } finally {
      setLoading(false)
    }
  }

  async function handleSendCode(e: React.FormEvent) {
    e.preventDefault()
    setPhoneError(null)
    setPhoneLoading(true)
    try {
      await Api.phoneLoginStart({ phoneLoginStartRequest: { phone } })
      setPhoneStep('code')
      setCode('')
    } catch {
      setPhoneError('Failed to send code. Check the phone number and try again.')
    } finally {
      setPhoneLoading(false)
    }
  }

  async function handleVerifyCode(e: React.FormEvent) {
    e.preventDefault()
    setPhoneError(null)
    setPhoneLoading(true)
    try {
      await Api.phoneLoginVerify({ phoneLoginVerifyRequest: { phone, code } })
      onPhoneLogin()
    } catch {
      setPhoneError('Invalid or expired code.')
    } finally {
      setPhoneLoading(false)
    }
  }

  async function handleResendCode() {
    setPhoneError(null)
    setPhoneLoading(true)
    try {
      await Api.phoneLoginStart({ phoneLoginStartRequest: { phone } })
    } catch {
      setPhoneError('Failed to resend code.')
    } finally {
      setPhoneLoading(false)
    }
  }

  function togglePhone() {
    setShowPhone(v => !v)
    setShowPassword(false)
    setPhoneError(null)
    setPhoneStep('entry')
    setPhone('')
    setCode('')
  }

  function togglePassword() {
    setShowPassword(v => !v)
    setShowPhone(false)
    setError(null)
  }

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-8">
          <img src="/bear_icon_transparent.svg" alt="" className="h-16 w-auto mb-4" />
          <h1 className="font-display font-black text-2xl tracking-widest text-bear uppercase leading-none">
            Kodiak Labs
          </h1>
          <p className="font-mono text-[10px] tracking-[0.25em] text-stone-400 uppercase mt-1">
            Citizen Science · Fisheries Data
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-stone-200 p-6 space-y-4 shadow-sm">
          {/* Google — primary action */}
          <button
            type="button"
            onClick={onGoogleSignIn}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl
              bg-bear hover:bg-bear-light active:bg-bear
              transition-colors cursor-pointer"
          >
            <GoogleG />
            <span className="flex-1 text-center font-mono text-[11px] tracking-[0.15em] uppercase font-semibold text-white pr-8">
              Sign in with Google
            </span>
          </button>

          {/* Password toggle */}
          <button
            type="button"
            onClick={togglePassword}
            className="w-full px-4 py-2.5 rounded-xl border border-stone-300
              bg-white hover:bg-stone-50
              font-mono text-[11px] tracking-[0.15em] uppercase font-semibold
              text-stone-700 hover:text-stone-900
              transition-colors cursor-pointer"
          >
            {showPassword ? 'Hide password' : 'Use password'}
          </button>

          {/* Expandable password form */}
          {showPassword && (
            <form onSubmit={handleSubmit} className="space-y-3">
                <div>
                  <label className="block font-mono text-[10px] tracking-[0.2em] uppercase text-stone-500 mb-1.5">
                    Email
                  </label>
                  <input
                    type="text"
                    value={username}
                    onChange={e => setUsername(e.target.value)}
                    className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2.5
                      font-mono text-sm text-stone-900 placeholder-stone-400
                      focus:outline-none focus:border-bear/60 transition-colors"
                    placeholder="you@example.com"
                    autoComplete="username"
                    required={showPassword}
                  />
                </div>
                <div>
                  <label className="block font-mono text-[10px] tracking-[0.2em] uppercase text-stone-500 mb-1.5">
                    Password
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2.5
                      font-mono text-sm text-stone-900 placeholder-stone-400
                      focus:outline-none focus:border-bear/60 transition-colors"
                    placeholder="••••••••"
                    autoComplete="current-password"
                    required={showPassword}
                  />
                </div>
                {error && (
                  <p className="font-mono text-[10px] text-coral">{error}</p>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full px-4 py-2.5 rounded-xl bg-bear text-white
                    font-mono text-[11px] tracking-[0.15em] uppercase font-semibold
                    hover:bg-bear-light transition-colors cursor-pointer
                    disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Signing in…' : 'Sign in'}
                </button>
                <button
                  type="button"
                  onClick={() => { window.location.hash = 'reset-password' }}
                  className="w-full font-mono text-[10px] tracking-[0.15em] uppercase text-stone-400 hover:text-stone-700 transition-colors"
                >
                  Forgot password?
                </button>
              </form>
          )}

          {/* Phone toggle */}
          <button
            type="button"
            onClick={togglePhone}
            className="w-full px-4 py-2.5 rounded-xl border border-stone-300
              bg-white hover:bg-stone-50
              font-mono text-[11px] tracking-[0.15em] uppercase font-semibold
              text-stone-700 hover:text-stone-900
              transition-colors cursor-pointer"
          >
            {showPhone ? 'Hide phone' : 'Use phone'}
          </button>

          {/* Expandable phone form */}
          {showPhone && (
            phoneStep === 'entry' ? (
                <form onSubmit={handleSendCode} className="space-y-3">
                  <div>
                    <label className="block font-mono text-[10px] tracking-[0.2em] uppercase text-stone-500 mb-1.5">
                      Phone number
                    </label>
                    <PhoneInput
                      country="US"
                      value={phone || undefined}
                      onChange={v => setPhone(v || '')}
                      className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2.5
                        font-mono text-sm text-stone-900 placeholder-stone-400
                        focus:outline-none focus:border-bear/60 transition-colors"
                      placeholder="(415) 555-0100"
                      autoComplete="tel"
                      required={showPhone}
                    />
                  </div>
                  {phoneError && (
                    <p className="font-mono text-[10px] text-coral">{phoneError}</p>
                  )}
                  <button
                    type="submit"
                    disabled={phoneLoading || !phoneIsValid}
                    className="w-full px-4 py-2.5 rounded-xl bg-bear text-white
                      font-mono text-[11px] tracking-[0.15em] uppercase font-semibold
                      hover:bg-bear-light transition-colors cursor-pointer
                      disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {phoneLoading ? 'Sending…' : 'Send code'}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleVerifyCode} className="space-y-3">
                  <p className="font-mono text-[10px] text-stone-500">
                    Code sent to {phone}
                  </p>
                  <div>
                    <label className="block font-mono text-[10px] tracking-[0.2em] uppercase text-stone-500 mb-1.5">
                      6-digit code
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={code}
                      onChange={e => setCode(e.target.value)}
                      className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2.5
                        font-mono text-sm text-stone-900 placeholder-stone-400
                        focus:outline-none focus:border-bear/60 transition-colors"
                      placeholder="123456"
                      autoComplete="one-time-code"
                      required
                    />
                  </div>
                  {phoneError && (
                    <p className="font-mono text-[10px] text-coral">{phoneError}</p>
                  )}
                  <button
                    type="submit"
                    disabled={phoneLoading}
                    className="w-full px-4 py-2.5 rounded-xl bg-bear text-white
                      font-mono text-[11px] tracking-[0.15em] uppercase font-semibold
                      hover:bg-bear-light transition-colors cursor-pointer
                      disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {phoneLoading ? 'Verifying…' : 'Verify'}
                  </button>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => { setPhoneStep('entry'); setPhoneError(null) }}
                      className="flex-1 font-mono text-[10px] tracking-[0.15em] uppercase text-stone-400 hover:text-stone-700 transition-colors"
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      onClick={handleResendCode}
                      disabled={phoneLoading}
                      className="flex-1 font-mono text-[10px] tracking-[0.15em] uppercase text-stone-400 hover:text-stone-700 transition-colors disabled:opacity-50"
                    >
                      Resend code
                    </button>
                  </div>
                </form>
              )
          )}
        </div>

        <div className="mt-6 text-center space-y-2">
          <p className="font-mono text-[10px] leading-relaxed text-stone-400">
            Kodiak is currently invite-only — invited users opt in to SMS
            notifications during onboarding.
          </p>
          <p className="font-mono text-[11px] tracking-[0.15em] uppercase font-semibold">
            <a href="/legal#about" className="text-bear hover:text-bear-light transition-colors">About</a>
            <span className="text-stone-300 mx-2">·</span>
            <a href="/legal#privacy" className="text-bear hover:text-bear-light transition-colors">Privacy</a>
            <span className="text-stone-300 mx-2">·</span>
            <a href="/legal#terms" className="text-bear hover:text-bear-light transition-colors">Terms</a>
            <span className="text-stone-300 mx-2">·</span>
            <a href="/legal#sms-program" className="text-bear hover:text-bear-light transition-colors">SMS Policy</a>
          </p>
          <p className="font-mono text-[10px] text-stone-400">
            © 2026 Net Don't Lie Enterprises, Inc. d/b/a Kodiak Labs
          </p>
        </div>
      </div>
    </div>
  )
}
